
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { useToast } from '@/hooks/use-toast';
import { Product } from '@/types/product';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthProvider';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Search,
  Pencil,
  Ban,
  Trash2,
  CheckCircle 
} from 'lucide-react';

const Products = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  const fetchProducts = async () => {
    try {
      console.log('Fetching products...');
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          profiles (
            username
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      console.log('Fetched products:', data);
      setProducts(data || []);
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Failed to load products",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkAdminAndFetchProducts = async () => {
      if (!user) {
        console.log('No user found, redirecting to login');
        navigate('/admin-web/login');
        return;
      }

      try {
        console.log('Checking admin status for user:', user.id);
        const { data: adminData, error: adminError } = await supabase
          .from('admins')
          .select('role')
          .eq('user_id', user.id)
          .maybeSingle();

        if (adminError || !adminData) {
          console.log('User is not an admin');
          toast({
            title: "Access Denied",
            description: "You don't have admin privileges",
            variant: "destructive"
          });
          navigate('/');
          return;
        }

        console.log('Admin role:', adminData.role);
        fetchProducts();
      } catch (error) {
        console.error('Error:', error);
        toast({
          title: "Error",
          description: "Failed to verify admin status",
          variant: "destructive",
        });
      }
    };

    checkAdminAndFetchProducts();
  }, [user, navigate, toast]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.brand.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async () => {
    if (!selectedProductId) return;

    try {
      console.log('Deleting product:', selectedProductId);
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', selectedProductId);

      if (error) {
        console.error('Error deleting product:', error);
        throw error;
      }

      await fetchProducts();
      
      toast({
        title: "Success",
        description: "Product deleted successfully",
      });

      setShowDeleteDialog(false);
      setSelectedProductId(null);
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: "Error",
        description: "Failed to delete product",
        variant: "destructive",
      });
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct) return;

    try {
      console.log('Updating product:', editingProduct);
      
      if (!editingProduct.name || !editingProduct.brand || !editingProduct.price) {
        toast({
          title: "Error",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase
        .from('products')
        .update({
          name: editingProduct.name,
          brand: editingProduct.brand,
          price: editingProduct.price,
          description: editingProduct.description || null
        })
        .eq('id', editingProduct.id)
        .select()
        .maybeSingle();

      if (error) {
        console.error('Error updating product:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Product not found');
      }

      console.log('Updated product:', data);
      await fetchProducts();

      toast({
        title: "Success",
        description: "Product updated successfully",
      });
      
      setEditingProduct(null);
      setDialogOpen(false);
    } catch (error) {
      console.error('Error updating product:', error);
      toast({
        title: "Error",
        description: "Failed to update product",
        variant: "destructive",
      });
    }
  };

  const handleToggleStatus = async (productId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'blocked' : 'active';
    try {
      console.log('Toggling product status:', { productId, newStatus });
      const { data, error } = await supabase
        .from('products')
        .update({ status: newStatus })
        .eq('id', productId)
        .select()
        .maybeSingle();

      if (error) {
        console.error('Error toggling status:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Product not found');
      }

      console.log('Updated product status:', data);
      await fetchProducts();

      toast({
        title: "Success",
        description: `Product ${newStatus === 'active' ? 'unblocked' : 'blocked'} successfully`,
      });
    } catch (error) {
      console.error('Error toggling product status:', error);
      toast({
        title: "Error",
        description: "Failed to update product status",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div className="p-4">Loading products...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Products</h1>
        <div className="relative w-64">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead>Reviews</TableHead>
              <TableHead>Created By</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredProducts.map((product) => (
              <TableRow key={product.id}>
                <TableCell>{product.name}</TableCell>
                <TableCell>{product.brand}</TableCell>
                <TableCell>${product.price}</TableCell>
                <TableCell>{product.rating || 0}</TableCell>
                <TableCell>{product.reviews_count || 0}</TableCell>
                <TableCell>{product.profiles?.username || 'System'}</TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    product.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {product.status || 'active'}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Dialog
                      open={dialogOpen && editingProduct?.id === product.id}
                      onOpenChange={(open) => {
                        setDialogOpen(open);
                        if (!open) setEditingProduct(null);
                      }}
                    >
                      <DialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingProduct(product);
                            setDialogOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Edit Product</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <label htmlFor="name">Name</label>
                            <Input
                              id="name"
                              value={editingProduct?.name || ''}
                              onChange={(e) => setEditingProduct(prev => prev ? { ...prev, name: e.target.value } : null)}
                            />
                          </div>
                          <div className="space-y-2">
                            <label htmlFor="brand">Brand</label>
                            <Input
                              id="brand"
                              value={editingProduct?.brand || ''}
                              onChange={(e) => setEditingProduct(prev => prev ? { ...prev, brand: e.target.value } : null)}
                            />
                          </div>
                          <div className="space-y-2">
                            <label htmlFor="price">Price</label>
                            <Input
                              id="price"
                              type="number"
                              value={editingProduct?.price || ''}
                              onChange={(e) => setEditingProduct(prev => prev ? { ...prev, price: parseFloat(e.target.value) } : null)}
                            />
                          </div>
                          <div className="space-y-2">
                            <label htmlFor="description">Description</label>
                            <Input
                              id="description"
                              value={editingProduct?.description || ''}
                              onChange={(e) => setEditingProduct(prev => prev ? { ...prev, description: e.target.value } : null)}
                            />
                          </div>
                          <Button onClick={handleUpdateProduct}>Save Changes</Button>
                        </div>
                      </DialogContent>
                    </Dialog>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleStatus(product.id, product.status || 'active')}
                    >
                      {product.status === 'blocked' ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <Ban className="h-4 w-4 text-red-600" />
                      )}
                    </Button>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedProductId(product.id);
                        setShowDeleteDialog(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the product.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Products;

